import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Paper, Typography, Box, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, Alert, CircularProgress,
    TextField, FormControl, InputLabel, Select, MenuItem, Grid, useTheme, Button
} from '@mui/material';
import {
    History as HistoryIcon,
    Gavel as GavelIcon,
    Event as EventIcon,
    Person as PersonIcon
} from '@mui/icons-material';
import api from '../services/api';

function HistoricoAcesso() {
    const navigate = useNavigate();
    const theme = useTheme();
    const [acessos, setAcessos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [filtroTipo, setFiltroTipo] = useState('');
    const [filtroData, setFiltroData] = useState(''); // Vazio = dia atual
    const [filtroUsuario, setFiltroUsuario] = useState('');
    const [usuarios, setUsuarios] = useState([]);

    useEffect(() => {
        loadUsuarios();
        loadAcessos();
    }, []);

    useEffect(() => {
        loadAcessos();
    }, [filtroTipo, filtroData, filtroUsuario]);

    const loadUsuarios = async () => {
        try {
            const response = await api.get('/usuarios/lista');
            setUsuarios(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar usuários:', error);
        }
    };

    const loadAcessos = async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            if (filtroTipo) {
                params.append('tipo_registro', filtroTipo);
            }
            if (filtroData) {
                params.append('data', filtroData);
            }
            if (filtroUsuario) {
                params.append('usuario_id', filtroUsuario);
            }

            const response = await api.get(`/auditoria/acessos-hoje?${params.toString()}`);
            setAcessos(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar histórico de acessos:', error);
            setError(error.response?.data?.error || 'Erro ao carregar histórico de acessos');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';
        try {
            // Se já está no formato YYYY-MM-DD (vindo do backend), converte diretamente para DD/MM/YYYY
            // Não usa new Date() para evitar problemas de timezone que podem alterar a data
            if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
                const [ano, mes, dia] = date.split('-');
                return `${dia}/${mes}/${ano}`;
            }
            // Se contém hora e data (YYYY-MM-DD HH:mm:ss), extrai apenas a data
            if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/.test(date)) {
                const [dataPart] = date.split(' ');
                const [ano, mes, dia] = dataPart.split('-');
                return `${dia}/${mes}/${ano}`;
            }
            // Caso contrário, tenta usar Date (para compatibilidade com outros formatos)
            const d = new Date(date);
            if (isNaN(d.getTime())) {
                return date;
            }
            // Usa métodos locais para evitar problemas de timezone
            const dia = String(d.getDate()).padStart(2, '0');
            const mes = String(d.getMonth() + 1).padStart(2, '0');
            const ano = d.getFullYear();
            return `${dia}/${mes}/${ano}`;
        } catch {
            return date;
        }
    };

    const formatTime = (time, created_at) => {
        if (!time) {
            // Se não tem hora separada, tenta usar created_at (timestamp completo)
            if (created_at) {
                try {
                    // created_at vem como "YYYY-MM-DD HH:MM:SS" (UTC)
                    const match = created_at.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
                    if (match) {
                        const [, ano, mes, dia, hora, minuto] = match;
                        // Cria uma data em UTC e converte para o timezone local
                        const dataUTC = new Date(`${ano}-${mes}-${dia}T${hora}:${minuto}:00Z`);
                        // Formata para o timezone local
                        const horaLocal = String(dataUTC.getHours()).padStart(2, '0');
                        const minutoLocal = String(dataUTC.getMinutes()).padStart(2, '0');
                        return `${horaLocal}:${minutoLocal}`;
                    }
                } catch (e) {
                    console.error('Erro ao formatar hora de created_at:', e);
                }
            }
            return '-';
        }
        try {
            // Se time já está no formato HH:MM:SS, verifica se precisa converter
            // Se created_at está disponível, usa ele para garantir conversão correta
            if (created_at) {
                try {
                    // Pega a data de data_acesso e combina com hora_acesso
                    // Assume que hora_acesso pode estar em UTC, então usa created_at para garantir
                    const match = created_at.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
                    if (match) {
                        const [, ano, mes, dia, hora, minuto] = match;
                        // Cria uma data em UTC e converte para o timezone local
                        const dataUTC = new Date(`${ano}-${mes}-${dia}T${hora}:${minuto}:00Z`);
                        // Formata para o timezone local
                        const horaLocal = String(dataUTC.getHours()).padStart(2, '0');
                        const minutoLocal = String(dataUTC.getMinutes()).padStart(2, '0');
                        return `${horaLocal}:${minutoLocal}`;
                    }
                } catch (e) {
                    // Se falhar, usa o time diretamente (fallback)
                    console.error('Erro ao formatar hora:', e);
                }
            }
            // Fallback: retorna apenas HH:MM do time fornecido
            if (time.includes(':')) {
                return time.substring(0, 5);
            }
            return time;
        } catch {
            return time;
        }
    };

    const getTipoColor = (tipo) => {
        switch (tipo) {
            case 'PROCESSO':
                return '#1976d2'; // Azul mais escuro
            case 'PRAZO':
                return '#42a5f5'; // Azul mais claro
            case 'CLIENTE':
                return '#2e7d32'; // Verde
            default:
                return 'default';
        }
    };

    const getTipoBgColor = (tipo) => {
        if (theme.palette.mode === 'dark') {
            // Cores escuras para tema escuro (com transparência para melhor legibilidade)
            switch (tipo) {
                case 'PROCESSO':
                    return 'rgba(25, 118, 210, 0.15)'; // Azul translúcido escuro
                case 'PRAZO':
                    return 'rgba(156, 39, 176, 0.15)'; // Roxo translúcido escuro
                default:
                    return 'transparent';
            }
        } else {
            // Cores claras para tema claro
            switch (tipo) {
                case 'PROCESSO':
                    return '#e3f2fd'; // Azul muito claro
                case 'PRAZO':
                    return '#f3e5f5'; // Roxo muito claro
                default:
                    return 'transparent';
            }
        }
    };

    const getOperacaoLabel = (operacao) => {
        switch (operacao) {
            case 'SELECT': return 'Visualização';
            case 'INSERT': return 'Criação';
            case 'UPDATE': return 'Atualização';
            case 'DELETE': return 'Exclusão';
            default: return operacao || 'Visualização';
        }
    };

    const getOperacaoColor = (operacao) => {
        switch (operacao) {
            case 'SELECT': return 'default';
            case 'INSERT': return 'success';
            case 'UPDATE': return 'primary';
            case 'DELETE': return 'error';
            default: return 'default';
        }
    };

    const handleRowClick = (acesso) => {
        if (acesso.tipo_registro === 'PROCESSO') {
            navigate(`/processos/ver/${acesso.registro_id}`);
        } else if (acesso.tipo_registro === 'PRAZO') {
            navigate(`/prazos/editar/${acesso.registro_id}`);
        } else if (acesso.tipo_registro === 'CLIENTE') {
            navigate(`/clientes/editar/${acesso.registro_id}`);
        }
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ mb: 3, p: 2, backgroundColor: 'primary.main', color: 'primary.contrastText', borderRadius: 2, boxShadow: 2, mt: 2 }}>
                <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    Histórico de Acessos
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                    Visualizações, criações, alterações e exclusões em Clientes, Processos e Prazos
                </Typography>
            </Box>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Data (vazio = hoje)"
                            type="date"
                            value={filtroData}
                            onChange={(e) => setFiltroData(e.target.value)}
                            size="small"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            helperText="Deixe vazio para ver o dia atual"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Tipo</InputLabel>
                            <Select
                                value={filtroTipo}
                                onChange={(e) => setFiltroTipo(e.target.value)}
                                label="Tipo"
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="PROCESSO">Processos</MenuItem>
                                <MenuItem value="PRAZO">Prazos</MenuItem>
                                <MenuItem value="CLIENTE">Clientes</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Usuário</InputLabel>
                            <Select
                                value={filtroUsuario}
                                onChange={(e) => setFiltroUsuario(e.target.value)}
                                label="Usuário"
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {usuarios.map((usuario) => (
                                    <MenuItem key={usuario.id} value={usuario.id}>
                                        {usuario.nome || usuario.username}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => {
                                setFiltroData('');
                                setFiltroTipo('');
                                setFiltroUsuario('');
                            }}
                            size="small"
                        >
                            Limpar Filtros
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Operação</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Registro</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell>Data/Hora</TableCell>
                            <TableCell align="right">Usuário</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : acessos.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        Nenhuma ocorrência registrada para o período selecionado
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            acessos.map((acesso) => (
                                <TableRow
                                    key={acesso.id}
                                    hover
                                    onClick={() => acesso.operacao !== 'DELETE' && handleRowClick(acesso)}
                                    sx={{
                                        cursor: acesso.operacao === 'DELETE' ? 'default' : 'pointer',
                                        backgroundColor: getTipoBgColor(acesso.tipo_registro),
                                        '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? (acesso.tipo_registro === 'PROCESSO' 
                                                    ? 'rgba(25, 118, 210, 0.25)' 
                                                    : acesso.tipo_registro === 'PRAZO'
                                                    ? 'rgba(156, 39, 176, 0.25)'
                                                    : 'rgba(46, 125, 50, 0.25)')
                                                : (acesso.tipo_registro === 'PROCESSO' 
                                                    ? '#bbdefb' 
                                                    : acesso.tipo_registro === 'PRAZO'
                                                    ? '#e1bee7'
                                                    : '#c8e6c9'),
                                        }
                                    }}
                                >
                                    <TableCell>
                                        <Chip
                                            label={getOperacaoLabel(acesso.operacao)}
                                            color={getOperacaoColor(acesso.operacao)}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={
                                                acesso.tipo_registro === 'PROCESSO' ? <GavelIcon /> : 
                                                acesso.tipo_registro === 'PRAZO' ? <EventIcon /> : 
                                                <PersonIcon />
                                            }
                                            label={
                                                acesso.tipo_registro === 'PROCESSO' ? 'Processo' : 
                                                acesso.tipo_registro === 'PRAZO' ? 'Prazo' : 
                                                'Cliente'
                                            }
                                            color={
                                                acesso.tipo_registro === 'PROCESSO' ? 'primary' : 
                                                acesso.tipo_registro === 'PRAZO' ? 'secondary' : 
                                                'success'
                                            }
                                            size="small"
                                            sx={{
                                                backgroundColor: getTipoColor(acesso.tipo_registro),
                                                color: 'white',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            #{acesso.registro_id}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {acesso.registro_descricao || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatDate(acesso.data_acesso)} - {formatTime(acesso.hora_acesso, acesso.created_at)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight="medium">
                                            {acesso.usuario_nome || 'Usuário'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Container>
    );
}

export default HistoricoAcesso;

